import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Radio } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, SafetyOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../utils/api';
import { UserType, LoginCredentials } from '../../types';
import TwoFactorSetup from '../TwoFactorSetup/TwoFactorSetup';
import EmailCodeVerification from '../EmailCodeVerification/EmailCodeVerification';
import { parseSessionExpiryTime } from '../../utils/sessionUtils';

// New Theme Colors
const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.bgPrimary};
  padding: 20px;
`;

const LoginCard = styled(Card)`
  width: 100%;
  max-width: 450px;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  background: ${COLORS.bgSecondary};
  border: 1px solid ${COLORS.border};
  
  .ant-card-body {
    padding: 40px;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Logo = styled.img<{ $clickable?: boolean }>`
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: transform 0.2s ease;
  
  &:hover {
    transform: ${props => props.$clickable ? 'scale(1.05)' : 'none'};
  }
  
  &:active {
    transform: ${props => props.$clickable ? 'scale(0.95)' : 'none'};
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${COLORS.textPrimary};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: ${COLORS.textSecondary};
  font-size: 14px;
  margin: 0;
`;

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-weight: 600;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-input,
  .ant-input-password {
    background-color: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    border-radius: 8px;
    padding: 8px 12px;
    
    &:hover,
    &:focus {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-input-affix-wrapper {
    background-color: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    border-radius: 8px;
    padding: 8px 12px;
    
    &:hover,
    &:focus,
    &-focused {
      border-color: ${COLORS.accent} !important;
    }
    
    .ant-input {
      padding: 0;
    }
    
    .anticon {
      color: ${COLORS.textSecondary};
    }
  }
  
  .ant-btn-primary {
    height: 48px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 16px;
    background: ${COLORS.accent};
    border: none;
    color: ${COLORS.bgPrimary};
    
    &:hover {
      background: #67e8f9; /* Cyan-300 */
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(34, 211, 238, 0.3);
    }
  }
`;

const UserTypeSelector = styled(Form.Item)`
  .ant-radio-group {
    width: 100%;
    display: flex;
    gap: 12px;
  }
  
  .ant-radio-button-wrapper {
    flex: 1;
    text-align: center;
    border-radius: 8px;
    height: 48px;
    line-height: 48px;
    font-weight: 600;
    background: ${COLORS.bgPrimary};
    border: 1px solid ${COLORS.border};
    color: ${COLORS.textSecondary};
    
    &:hover {
      color: ${COLORS.accent};
      border-color: ${COLORS.accent};
    }
    
    &.ant-radio-button-wrapper-checked {
      background: ${COLORS.accentDim};
      border-color: ${COLORS.accent};
      color: ${COLORS.accent};
      
      &::before {
        display: none;
      }
    }
  }
`;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get continue parameter for redirect after login
  const continueRoute = searchParams.get('continue');
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [userType, setUserType] = useState<UserType>(UserType.Merchant);
  
  // 2FA setup state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState<{ secret: string; uri: string; qr: string } | null>(null);
  const [pendingCredentials, setPendingCredentials] = useState<{ userName: string; password: string; userType: UserType; twoFaCode?: number } | null>(null);
  
  // Email code verification state
  const [showEmailCodeVerification, setShowEmailCodeVerification] = useState(false);
  const [pendingEmailCredentials, setPendingEmailCredentials] = useState<{ userName: string; password: string; userType: UserType; twoFaCode?: number; deviceId?: string } | null>(null);
  
  // 2FA code required state (for users who already have 2FA enabled)
  const [requiresTwoFactorCode, setRequiresTwoFactorCode] = useState(false);

  const normalizeEmailCode = (code?: string) => {
    const normalized = code?.trim().toUpperCase();
    return normalized ? normalized : undefined;
  };

  // Check if user is already logged in and redirect
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const accessSecret = localStorage.getItem('accessSecret');
    const userType = localStorage.getItem('userType');
    const sessionExpiryTime = localStorage.getItem('sessionExpiryTime');

    if (accessToken && accessSecret && userType) {
      // Check if session is still valid
      if (sessionExpiryTime) {
        const expiryTimestamp = parseInt(sessionExpiryTime, 10);
        if (Date.now() < expiryTimestamp) {
          // Session is valid, redirect to dashboard
          if (userType === UserType.Administrator) {
            navigate('/kivu');
          } else {
            navigate('/igwena');
          }
          return;
        } else {
          // Session expired, clear storage
          localStorage.removeItem('accessToken');
          localStorage.removeItem('accessSecret');
          localStorage.removeItem('userType');
          localStorage.removeItem('sessionExpiryTime');
        }
      } else {
        // No expiry time stored, redirect anyway
        if (userType === UserType.Administrator) {
          navigate('/kivu');
        } else {
          navigate('/igwena');
        }
        return;
      }
    }

    // Check URL parameter on mount and when it changes
    const urlUserType = searchParams.get('userType');
    if (urlUserType === UserType.Administrator || urlUserType === UserType.Merchant) {
      // URL parameter takes precedence
      const newUserType = urlUserType as UserType;
      setUserType(newUserType);
      setIsAdminMode(newUserType === UserType.Administrator);
      form.setFieldsValue({ userType: newUserType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only react to URL param changes

  // Handle logo click - 7 clicks to enable admin mode
  const handleLogoClick = () => {
    // Don't allow logo clicks to override URL parameters
    if (searchParams.get('userType')) {
      return;
    }

    // Once admin mode is enabled, ignore further clicks (use the button to switch back)
    if (isAdminMode) {
      return;
    }

    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);

    if (newCount === 7) {
      setIsAdminMode(true);
      setUserType(UserType.Administrator);
      form.setFieldsValue({ userType: UserType.Administrator });
      message.success('You are now admin', 3);
      setLogoClickCount(0); // Reset counter after enabling
    }
  };

  const onFinish = async (values: unknown) => {
    const formValues = values as LoginCredentials;
    setLoading(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        userName: formValues.userName,
        password: formValues.password,
        userType: formValues.userType,
        twoFaCode: formValues.twoFaCode ? parseInt(formValues.twoFaCode.toString()) : undefined,
        emailCode: normalizeEmailCode(formValues.emailCode),
      });

      // Check response status and action
      const responseData = response.data;
      const statusCode = responseData?.statusCode || response.status;
      const action = responseData?.action;
      const data = responseData?.data;

      // Handle new device authentication required (statusCode 206)
      if (statusCode === 206 && action?.require_email_code) {
        // Show email code verification modal
        setPendingEmailCredentials({
          userName: formValues.userName,
          password: formValues.password,
          userType: formValues.userType,
          twoFaCode: formValues.twoFaCode ? parseInt(formValues.twoFaCode.toString()) : undefined,
          deviceId: data?.deviceId,
        });
        setShowEmailCodeVerification(true);
        setLoading(false);
        return;
      }

      // Handle 2FA code required (statusCode 206 with requires_two_factor_code)
      if (statusCode === 206 && action?.message === 'requires_two_factor_code') {
        // User has 2FA enabled, need to provide 2FA code
        setRequiresTwoFactorCode(true);
        message.warning('Please enter your 2FA code to continue', 5);
        setLoading(false);
        // Focus on 2FA code input
        setTimeout(() => {
          const twoFaInput = document.querySelector('input[name="twoFaCode"]') as HTMLInputElement;
          if (twoFaInput) {
            twoFaInput.focus();
          }
        }, 100);
        return;
      }

      // Handle 2FA setup required (statusCode 206 with requires_two_factor_enabled)
      if (statusCode === 206 && action?.message === 'requires_two_factor_enabled' && data?.twoFactorSecret) {
        // Show 2FA setup modal
        setTwoFactorSecret(data.twoFactorSecret);
        setPendingCredentials({
          userName: formValues.userName,
          password: formValues.password,
          userType: formValues.userType,
          twoFaCode: formValues.twoFaCode ? parseInt(formValues.twoFaCode.toString()) : undefined,
        });
        setShow2FASetup(true);
        setLoading(false);
        return;
      }

      if (data) {
        const { accessToken, accessSecret, twoFactorSecret: twoFactorSecretData } = data;
        
        // Check if 2FA setup is required (first-time user without 2FA)
        if (twoFactorSecretData && (!accessToken || !accessSecret)) {
          // Show 2FA setup modal
          setTwoFactorSecret(twoFactorSecretData);
          setPendingCredentials({
            userName: formValues.userName,
            password: formValues.password,
            userType: formValues.userType,
            twoFaCode: formValues.twoFaCode ? parseInt(formValues.twoFaCode.toString()) : undefined,
          });
          setShow2FASetup(true);
          setLoading(false);
          return;
        }

        // Login successful
        if (accessToken && accessSecret) {
          // Store tokens
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('accessSecret', accessSecret);
          localStorage.setItem('userType', formValues.userType);
          
          // Store session expiry time
          if (data?.sessionExpiryTime) {
            const expiryTimestamp = parseSessionExpiryTime(data.sessionExpiryTime);
            localStorage.setItem('sessionExpiryTime', expiryTimestamp.toString());
          }
          
          // Reset 2FA code required state
          setRequiresTwoFactorCode(false);
          
          message.success('Login successful!');
          
          // Redirect to continue route if provided, otherwise default based on user type
          if (continueRoute) {
            try {
              const decodedRoute = decodeURIComponent(continueRoute);
              navigate(decodedRoute);
            } catch (error) {
              console.error('Error decoding continue route:', error);
              // Fallback to default route if decoding fails
              if (formValues.userType === UserType.Administrator) {
                navigate('/kivu');
              } else {
                navigate('/igwena');
              }
            }
          } else {
            // Redirect based on user type
            if (formValues.userType === UserType.Administrator) {
              navigate('/kivu');
            } else {
              navigate('/igwena');
            }
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      message.error(errorMessage);
      // If error occurs and 2FA was required, keep the state so user can retry
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (code: string) => {
    if (!pendingCredentials) {
      throw new Error('No pending credentials');
    }

    setLoading(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        userName: pendingCredentials.userName,
        password: pendingCredentials.password,
        userType: pendingCredentials.userType,
        twoFaCode: parseInt(code),
      });

      // Check for email code requirement after 2FA
      const responseData = response.data;
      const statusCode = responseData?.statusCode || response.status;
      const action = responseData?.action;

      if (statusCode === 206 && action?.require_email_code) {
        // Close 2FA setup modal and show email code verification
        setShow2FASetup(false);
        setTwoFactorSecret(null);
        setPendingEmailCredentials({
          userName: pendingCredentials.userName,
          password: pendingCredentials.password,
          userType: pendingCredentials.userType,
          twoFaCode: parseInt(code),
          deviceId: responseData?.data?.deviceId,
        });
        setPendingCredentials(null);
        setShowEmailCodeVerification(true);
        setLoading(false);
        return;
      }

      if (responseData?.data) {
        const { accessToken, accessSecret } = responseData.data;
        
        if (accessToken && accessSecret) {
          // Store tokens
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('accessSecret', accessSecret);
          localStorage.setItem('userType', pendingCredentials.userType);
          
          // Store session expiry time
          if (responseData?.data?.sessionExpiryTime) {
            const expiryTimestamp = parseSessionExpiryTime(responseData.data.sessionExpiryTime);
            localStorage.setItem('sessionExpiryTime', expiryTimestamp.toString());
          }
          
          // Reset 2FA code required state
          setRequiresTwoFactorCode(false);
          
          // Close 2FA setup modal
          setShow2FASetup(false);
          setTwoFactorSecret(null);
          setPendingCredentials(null);
          
          // Redirect to continue route if provided, otherwise default based on user type
          if (continueRoute) {
            try {
              const decodedRoute = decodeURIComponent(continueRoute);
              navigate(decodedRoute);
            } catch (error) {
              console.error('Error decoding continue route:', error);
              // Fallback to default route if decoding fails
              if (pendingCredentials.userType === UserType.Administrator) {
                navigate('/kivu');
              } else {
                navigate('/igwena');
              }
            }
          } else {
            // Redirect based on user type
            if (pendingCredentials.userType === UserType.Administrator) {
              navigate('/kivu');
            } else {
              navigate('/igwena');
            }
          }
        } else {
          throw new Error('Invalid response from server');
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to verify 2FA code';
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCodeVerify = async (code: string) => {
    if (!pendingEmailCredentials) {
      throw new Error('No pending credentials');
    }

    setLoading(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        userName: pendingEmailCredentials.userName,
        password: pendingEmailCredentials.password,
        userType: pendingEmailCredentials.userType,
        twoFaCode: pendingEmailCredentials.twoFaCode,
        emailCode: normalizeEmailCode(code),
      });

      const responseData = response.data;
      const statusCode = responseData?.statusCode || response.status;
      const action = responseData?.action;
      const data = responseData?.data;

      // Check if 2FA code is required (statusCode 206 with requires_two_factor_code)
      if (statusCode === 206 && action?.message === 'requires_two_factor_code') {
        // User has 2FA enabled, need to provide 2FA code
        // Close email verification modal and show 2FA code field in main form
        setShowEmailCodeVerification(false);
        setRequiresTwoFactorCode(true);
        // Preserve credentials in form so user can enter 2FA code and resubmit
        form.setFieldsValue({
          userName: pendingEmailCredentials.userName,
          password: pendingEmailCredentials.password,
          userType: pendingEmailCredentials.userType,
        });
        setPendingEmailCredentials(null);
        message.warning('Please enter your 2FA code to continue', 5);
        setLoading(false);
        // Focus on 2FA code input
        setTimeout(() => {
          const twoFaInput = document.querySelector('input[name="twoFaCode"]') as HTMLInputElement;
          if (twoFaInput) {
            twoFaInput.focus();
          }
        }, 100);
        return;
      }

      // Check if 2FA setup is required (statusCode 206 with requires_two_factor_enabled)
      if (statusCode === 206 && action?.message === 'requires_two_factor_enabled' && data?.twoFactorSecret) {
        // Show 2FA setup modal
        setShowEmailCodeVerification(false);
        setTwoFactorSecret(data.twoFactorSecret);
        setPendingCredentials({
          userName: pendingEmailCredentials.userName,
          password: pendingEmailCredentials.password,
          userType: pendingEmailCredentials.userType,
          twoFaCode: pendingEmailCredentials.twoFaCode,
        });
        setPendingEmailCredentials(null);
        setShow2FASetup(true);
        setLoading(false);
        return;
      }

      if (data) {
        const { accessToken, accessSecret, twoFactorSecret: twoFactorSecretData } = data;
        
        // Check if 2FA setup is still required after email verification
        if (twoFactorSecretData && (!accessToken || !accessSecret)) {
          // Show 2FA setup modal
          setShowEmailCodeVerification(false);
          setTwoFactorSecret(twoFactorSecretData);
          setPendingCredentials({
            userName: pendingEmailCredentials.userName,
            password: pendingEmailCredentials.password,
            userType: pendingEmailCredentials.userType,
            twoFaCode: pendingEmailCredentials.twoFaCode,
          });
          setPendingEmailCredentials(null);
          setShow2FASetup(true);
          setLoading(false);
          return;
        }

        if (accessToken && accessSecret) {
          // Store tokens
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('accessSecret', accessSecret);
          localStorage.setItem('userType', pendingEmailCredentials.userType);
          
          // Store session expiry time
          if (data?.sessionExpiryTime) {
            const expiryTimestamp = parseSessionExpiryTime(data.sessionExpiryTime);
            localStorage.setItem('sessionExpiryTime', expiryTimestamp.toString());
          }
          
          // Reset 2FA code required state
          setRequiresTwoFactorCode(false);
          
          // Close email code verification modal
          setShowEmailCodeVerification(false);
          setPendingEmailCredentials(null);
          
          // Redirect to continue route if provided, otherwise default based on user type
          if (continueRoute) {
            try {
              const decodedRoute = decodeURIComponent(continueRoute);
              navigate(decodedRoute);
            } catch (error) {
              console.error('Error decoding continue route:', error);
              // Fallback to default route if decoding fails
              if (pendingEmailCredentials.userType === UserType.Administrator) {
                navigate('/kivu');
              } else {
                navigate('/igwena');
              }
            }
          } else {
            // Redirect based on user type
            if (pendingEmailCredentials.userType === UserType.Administrator) {
              navigate('/kivu');
            } else {
              navigate('/igwena');
            }
          }
        } else {
          // If we have twoFactorSecret, it means 2FA setup is required
          if (twoFactorSecretData) {
            setShowEmailCodeVerification(false);
            setTwoFactorSecret(twoFactorSecretData);
            setPendingCredentials({
              userName: pendingEmailCredentials.userName,
              password: pendingEmailCredentials.password,
              userType: pendingEmailCredentials.userType,
              twoFaCode: pendingEmailCredentials.twoFaCode,
            });
            setPendingEmailCredentials(null);
            setShow2FASetup(true);
            setLoading(false);
            return;
          }
          throw new Error('Invalid response from server');
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to verify email code';
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoContainer>
          <Logo 
            src="/icon.svg" 
            alt="Switch Wallet Logo"
            $clickable={true}
            onClick={handleLogoClick}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <Title>Switch Wallet</Title>
          <Subtitle>
            {userType === UserType.Administrator ? 'Administrator Dashboard' : 'Merchant Dashboard'}
          </Subtitle>
        </LogoContainer>

        <StyledForm
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          initialValues={{
            userType: userType,
          }}
          size="large"
        >
          {/* Only show user type selector when admin mode is enabled or URL param is Administrator */}
          {(isAdminMode || userType === UserType.Administrator) ? (
            <UserTypeSelector name="userType" rules={[{ required: true, message: 'Please select user type' }]}>
              <Radio.Group>
                <Radio.Button value={UserType.Administrator}>Administrator</Radio.Button>
                <Radio.Button value={UserType.Merchant}>Merchant</Radio.Button>
              </Radio.Group>
            </UserTypeSelector>
          ) : (
            /* Hidden field to ensure userType is always set in the form when selector is hidden */
            <Form.Item name="userType" hidden initialValue={UserType.Merchant}>
              <Input type="hidden" />
            </Form.Item>
          )}

          <Form.Item
            name="userName"
            label="Email Address"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password autoComplete="current-password"
              prefix={<LockOutlined />}
              placeholder="Enter your password"
            />
          </Form.Item>

          <Form.Item
            name="twoFaCode"
            label={requiresTwoFactorCode ? "2FA Code (Required)" : "2FA Code (if enabled)"}
            rules={[
              ...(requiresTwoFactorCode ? [{ required: true, message: 'Please enter your 2FA code' }] : []),
              { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit code' }
            ]}
            help={requiresTwoFactorCode ? "Please enter the 6-digit code from your authenticator app" : undefined}
          >
            <Input
              prefix={<SafetyOutlined />}
              placeholder="Enter 6-digit code"
              maxLength={6}
              status={requiresTwoFactorCode ? 'warning' : undefined}
              onChange={(e) => {
                // Reset the requiresTwoFactorCode state when user starts typing
                if (requiresTwoFactorCode && e.target.value.length > 0) {
                  // Keep it true until they submit successfully
                }
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              icon={<LoginOutlined />}
              loading={loading}
            >
              Sign In
            </Button>
          </Form.Item>

          {userType === UserType.Merchant && (
            <div style={{ textAlign: 'center', marginTop: -8 }}>
              <span style={{ color: '#94A3B8', fontSize: 14 }}>
                Don't have an account?{' '}
                <a
                  href="/register"
                  onClick={(e) => { e.preventDefault(); navigate('/register'); }}
                  style={{ color: '#22D3EE', fontWeight: 600 }}
                >
                  Create Account
                </a>
              </span>
            </div>
          )}
        </StyledForm>
      </LoginCard>

      {/* 2FA Setup Modal */}
      {twoFactorSecret && (
        <TwoFactorSetup
          visible={show2FASetup}
          qrCode={twoFactorSecret.qr}
          uri={twoFactorSecret.uri}
          secret={twoFactorSecret.secret}
          onVerify={handle2FAVerify}
          onCancel={() => {
            setShow2FASetup(false);
            setTwoFactorSecret(null);
            setPendingCredentials(null);
          }}
        />
      )}

      {/* Email Code Verification Modal */}
      <EmailCodeVerification
        visible={showEmailCodeVerification}
        email={pendingEmailCredentials?.userName}
        onVerify={handleEmailCodeVerify}
        onCancel={() => {
          setShowEmailCodeVerification(false);
          setPendingEmailCredentials(null);
        }}
      />
    </LoginContainer>
  );
};

export default Login;

